import React, { useState } from "react";
import {
  FilterValue,
  SelectFilter,
  SelectOption,
  StringFilterValue,
} from "../../types/filter";
import { FixaListView } from "../../../wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "../../../wrappers/FixaListView/FixaListViewItem";
import FixaSearch from "../../../wrappers/FixaSearch/FixaSearch";
import { useTranslation } from "@coworker/locales";
import SelectListViewCss from "./SelectListView.module.css";
import { localeIncludes } from "./SelectListView.helper";
import {
  getSelectOptionKey,
  isCustomPeriodOption,
  isSelectGroup,
  isSelectOption,
} from "../../utils/filter/selectoption";
import { CategoryListItem } from "./CategoryListItem";
import { GroupSelectListItem } from "./GroupSelectListItem";
import { CustomPeriod } from "../CustomPeriod/CustomPeriod";

interface SelectListViewProps {
  prefix: string;
  filter: SelectFilter;
  selected: FilterValue;
  onChange: (option: SelectOption) => void;
  isModal?: boolean;
}

export const SelectListView = ({
  prefix,
  filter,
  onChange,
  selected,
  isModal = true,
}: SelectListViewProps) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const { id, title, options, showSearch } = filter;

  return (
    <div>
      {showSearch && (
        <FixaSearch
          className={SelectListViewCss["search-input"] ?? ""}
          id={`listview-search-${prefix}-${id}`}
          value={searchInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchInput(e.target.value);
          }}
          onClear={() => setSearchInput("")}
          placeholder={t("searchFilterOptionsString", { title })}
          data-testid={`listview-search-${prefix}-${id}`}
        />
      )}
      <FixaListView
        id={`listview-${prefix}-${id}`}
        data-testid={`listview-${prefix}-${id}`}
        size="small"
        showDivider={false}
      >
        {options
          .filter(
            (option) =>
              !isSelectOption(option) ||
              selected.value === option.value ||
              localeIncludes(option.title, searchInput)
          )
          .map((option, idx) => {
            if (isSelectGroup(option)) {
              return (
                <GroupSelectListItem
                  key={getSelectOptionKey(idx, option)}
                  item={option}
                  prefix={prefix}
                  selected={selected}
                  searchInput={searchInput}
                  onChange={onChange}
                  defaultValue={filter.defaultValue as StringFilterValue}
                />
              );
            } else if (isSelectOption(option)) {
              const { value } = option;
              const isSelected = selected.value === value;

              return [
                <FixaListViewItem
                  key={getSelectOptionKey(idx, option)}
                  inset
                  onChange={() => {
                    onChange(option);
                  }}
                  control="radiobutton"
                  controlProps={{
                    value: isSelected ? "ON" : "OFF",
                    checked: isSelected,
                  }}
                  showDivider={false}
                  title={option.title}
                  data-testid={`listview-option-${prefix}-${value}`}
                />,
                isModal && isSelected && isCustomPeriodOption(option) && (
                  <CustomPeriod
                    key={`${getSelectOptionKey(idx, option)}-custom`}
                    onChange={onChange}
                    selected={option}
                  />
                ),
              ];
            } else {
              return (
                <CategoryListItem
                  key={getSelectOptionKey(idx, option)}
                  item={option}
                  prefix={prefix}
                />
              );
            }
          })}
      </FixaListView>
    </div>
  );
};
